import { cn } from '@/lib/cn';

/*
  Label and value pairs, as a real <dl>.

  A div grid with two spans reads as an undifferentiated run of text. A definition list tells
  assistive technology that "Serial number" names "MOCK-000001", which is the whole content of
  an overview panel, and it costs nothing.

  The empty case is the part worth being deliberate about. A null column in the schema is a
  fact, not a missing field (see the note at the top of src/lib/api/types.ts), so a value the
  agent has not reported renders as explicit placeholder text rather than as a blank cell. A
  blank cell is indistinguishable from a rendering bug.
*/

export interface DataItem {
  readonly label: string;
  readonly value: React.ReactNode;
  /** Shown when value is null or undefined. Defaults to "Not reported". */
  readonly emptyText?: string;
  /** Render the value in the monospace face: hostnames, serials, IPs, versions. */
  readonly mono?: boolean;
  readonly span?: 1 | 2;
}

export function DataList({
  items,
  columns = 2,
  className,
}: {
  readonly items: readonly DataItem[];
  readonly columns?: 1 | 2 | 3;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-3.5',
        columns === 1 ? 'grid-cols-1' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3',
        className,
      )}
    >
      {items.map((item) => {
        const empty = item.value === null || item.value === undefined || item.value === '';
        return (
          <div key={item.label} className={cn('min-w-0', item.span === 2 && 'sm:col-span-2')}>
            <dt className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
              {item.label}
            </dt>
            <dd
              className={cn(
                'mt-0.5 text-sm break-words',
                empty ? 'text-fg-subtle italic' : 'text-fg',
                !empty && item.mono === true && 'font-mono text-[13px]',
              )}
            >
              {empty ? (item.emptyText ?? 'Not reported') : item.value}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
